import React from "react";
import { Socket, io } from "socket.io-client";
import msgpackParser from "socket.io-msgpack-parser";
import { AnimatePresence, motion } from "framer-motion";
import useAuth from "../hook/useAuth";
import { useTranslation } from "react-i18next";
import useAppEvent from "../hook/app/useAppEvent";
import useStoreToast from "store/useStoreToast";
import useStorePlayer from "store/useStorePlayer";
import SignupModal from "component/Signup/SignupModal";
import getPublicAssetUrl from "utils/PublicAsset";
import hostIntegration from "integrations/host";
import HostConfig from "integrations/host/HostConfig";

type AppContextValue = {
    socket: Socket | null,
    setSocket: React.Dispatch<React.SetStateAction<Socket | null>>,
    token: string | null,
    setToken: React.Dispatch<React.SetStateAction<string | null>>,
    nonce: number,
    setNonce: React.Dispatch<React.SetStateAction<number>>,
    viewportWidth: number,
    platform: "mobile" | "tablet" | "desktop",
    isConnected: boolean,
    isStandalone: boolean,
};

const detectStandalone = (): boolean => {
    if(typeof window === "undefined") {
        return false;
    }

    return window.matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
};

const AppContextDefaultValue = {} as AppContextValue;

export const AppContext = React.createContext(AppContextDefaultValue);

export const AppProvider = (props: { children: React.ReactNode }) => {
    const [ socket, setSocket ] = React.useState<Socket | null>(null);
    const [ token, setToken ] = React.useState<string | null>(null);
    const [ nonce, setNonce ] = React.useState<number>(0);
    const [ viewportWidth, setViewportWidth ] = React.useState<number>(0);
    const [ platform, setPlatform ] = React.useState<"mobile" | "tablet" | "desktop">("mobile");
    const [ isConnected, setIsConnected ] = React.useState(false);
    const [ isStandalone, setIsStandalone ] = React.useState<boolean>(detectStandalone);

    React.useEffect(() => {
        const mq = window.matchMedia("(display-mode: standalone)");
        const handler = () => setIsStandalone(detectStandalone());

        mq.addEventListener("change", handler);

        return () => mq.removeEventListener("change", handler);
    }, []);
    const [ hasConnectedOnce, setHasConnectedOnce ] = React.useState(false);
    const [ showSplash, setShowSplash ] = React.useState(false);
    const activeConnectionNonce = React.useRef<number | null>(null);
    const hasConnectionErrored = React.useRef(false);
    const hasResolvedInitialHostAuthentication = React.useRef(false);
    const tokenRef = React.useRef<string | null>(token);

    const { t, i18n } = useTranslation();
    const { isAuthReady, clearSession } = useAuth(socket, setToken);

    tokenRef.current = token;

    React.useEffect(() => {
        useStorePlayer.getState().register({ setToken, setNonce });
    }, [setToken, setNonce]);

    React.useEffect(() => hostIntegration.onPlayerAuthenticated(() => {
        useStorePlayer.getState().refreshSession();
    }), []);

    useAppEvent(socket);

    React.useEffect(() => {
        if(!isAuthReady) {
            return;
        }

        const socketUrl = process.env.REACT_APP_WEBSOCKET_URL || "http://192.168.1.47:30000";
        const isProduction = window.location.hostname.endsWith("last.cards") || /\blast\.cards/.test(socketUrl);

        const nextSocket = io(socketUrl, {
            parser: isProduction ? msgpackParser : undefined,
            transports: ["websocket", "polling"],
            tryAllTransports: true,
            autoConnect: false,
            auth: async (cb) => {
                const playerCredential = await hostIntegration.getPlayerCredential();
                const discardPersistedSession = HostConfig.host === "crazygames"
                    && !hasResolvedInitialHostAuthentication.current
                    && playerCredential.status === "not-authenticated";

                if(playerCredential.status !== "unavailable") {
                    hasResolvedInitialHostAuthentication.current = true;
                }

                if(discardPersistedSession) {
                    clearSession();
                }

                cb({
                    token: discardPersistedSession ? null : tokenRef.current,
                    hostCredential: playerCredential.credential,
                    language: localStorage.getItem("i18nextLng") || i18n.language,
                });
            },
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnection: true,
        });

        activeConnectionNonce.current = null;
        setSocket(nextSocket);

        return () => {
            nextSocket.disconnect();
        };
    }, [isAuthReady, clearSession, i18n]);

    React.useEffect(() => {
        if(!socket) {
            return;
        }

        const showToast = useStoreToast.getState().show;

        const onConnect = () => {
            if(hasConnectionErrored.current) {
                showToast(t("error.server_reconnected"), "success");
                hasConnectionErrored.current = false;
            }

            setIsConnected(true);
            setHasConnectedOnce(true);
        };

        const onDisconnect = () => setIsConnected(false);

        const onConnectError = () => {
            if(!hasConnectionErrored.current) {
                showToast(t("error.server_unavailable"), "error");
                hasConnectionErrored.current = true;
            }
        };

        if(socket.connected) {
            setIsConnected(true);
            setHasConnectedOnce(true);
        }

        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onConnectError);

        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("connect_error", onConnectError);
        };
    }, [socket]);

    React.useEffect(() => {
        if(
            !isAuthReady
            || !socket
            || activeConnectionNonce.current === nonce
        ) {
            return;
        }

        const isReconnect = activeConnectionNonce.current !== null;

        activeConnectionNonce.current = nonce;
        setIsConnected(false);

        if(isReconnect) {
            socket.disconnect();
        }

        socket.connect();
    }, [socket, nonce, isAuthReady]);

    React.useEffect(() => {
        if(hasConnectedOnce) {
            return;
        }

        const timer = setTimeout(() => setShowSplash(true), 1000);
        return () => clearTimeout(timer);
    }, [hasConnectedOnce]);

    React.useEffect(() => {
        let rafId: number | null = null;

        const applyViewport = () => {
            rafId = null;
            const width = window.innerWidth;

            setViewportWidth(width);
            setPlatform(width >= 1024 ? "desktop" : width >= 768 ? "tablet" : "mobile");
        };

        const scheduleApply = () => {
            if(rafId !== null) {
                return;
            }

            rafId = requestAnimationFrame(applyViewport);
        };

        applyViewport();

        window.addEventListener("resize", scheduleApply);
        window.addEventListener("orientationchange", scheduleApply);

        return () => {
            if(rafId !== null) {
                cancelAnimationFrame(rafId);
            }

            window.removeEventListener("resize", scheduleApply);
            window.removeEventListener("orientationchange", scheduleApply);
        };
    }, []);

    React.useLayoutEffect(() => {
        if(!["mobile", "tablet"].includes(platform)) {
            return;
        }

        const onVisibilityChange = () => {
            if(document.visibilityState === "visible") {
                socket?.connect();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [platform, socket]);

    return (
        <AppContext.Provider value={{ socket, setSocket, token, setToken, nonce, setNonce, viewportWidth, platform, isConnected, isStandalone }}>
            {props.children}
            <SignupModal />
            <AnimatePresence>
                {showSplash && !hasConnectedOnce && (
                    <motion.div
                        key="splash"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
                        style={{ background: "linear-gradient(180deg, rgb(25,25,35) 0%, rgb(15,15,22) 100%)" }}
                    >
                        <img
                            src={getPublicAssetUrl("assets/logo.webp")}
                            alt="Last"
                            className="logo-heartbeat w-[75vw] max-w-[400px] h-auto drop-shadow-2xl"
                            style={{ filter: "drop-shadow(0 0 60px rgba(34,211,238,0.3))" }}
                            draggable={false}
                        />
                        <div className="mt-8 w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                    </motion.div>
                )}
            </AnimatePresence>
        </AppContext.Provider>
    )
};
